
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { AppRole } from '@/types/auth';

export interface InvitationDetails {
  id: string;
  email: string;
  roles: AppRole[];
  firstName?: string;
  lastName?: string;
  organizationName?: string;
  organization_id?: string;
  expires_at: string;
}

export const useInvitationHandler = (invitationId: string | null) => {
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (invitationId) {
      fetchInvitation(invitationId);
    } else {
      // Reset state when no invitation ID
      setInvitation(null);
      setLoading(false);
      setError(null);
    }
  }, [invitationId]);

  const fetchInvitation = async (id: string) => {
    setLoading(true);
    setError(null);
    setInvitation(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('user_provisioning_requests')
        .select('*, organizations(name)')
        .eq('id', id)
        .eq('status', 'approved')
        .is('provisioned_at', null)
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          setError('Invitation not found. Please check the invitation link.');
        } else {
          setError(`Failed to load invitation details: ${fetchError.message}`);
        }
        return;
      }

      if (!data) {
        setError('Invitation not found. Please check the invitation link.');
        return;
      }

      // Check if invitation is expired (7 days from creation)
      const expiryDate = new Date(data.created_at);
      expiryDate.setDate(expiryDate.getDate() + 7);
      if (expiryDate < new Date()) {
        setError('This invitation has expired. Please request a new invitation.');
        return;
      }

      // Map the data to our interface
      const invitationDetails: InvitationDetails = {
        id: data.id,
        email: data.email,
        roles: data.requested_roles,
        firstName: data.first_name,
        lastName: data.last_name,
        organizationName: data.organizations?.name,
        organization_id: data.organization_id,
        expires_at: expiryDate.toISOString()
      };

      setInvitation(invitationDetails);
    } catch (err) {
      setError('An unexpected error occurred while loading the invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const processInvitationSignup = async (email: string, password: string, firstName: string, lastName: string) => {
    if (!invitation) {
      throw new Error('No invitation found');
    }

    // Verify email matches invitation
    if (email !== invitation.email) {
      throw new Error('Email must match the invitation email');
    }

    // Validate invitation has organization
    if (!invitation.organization_id) {
      return { 
        error: { 
          message: 'This invitation is missing organization information. Please contact support.' 
        } 
      };
    }

    try {
      const { authRegistrationService } = await import('@/services/authRegistrationService');
      const result = await authRegistrationService.registerWithInvitation(
        invitation.id,
        email,
        password,
        firstName || invitation.firstName || '',
        lastName || invitation.lastName || ''
      );

      if (!result.success) {
        return { error: { message: result.error } };
      }

      // For invited users, redirect to dashboard since they inherit organization subscription
      // Give a brief moment for auth state to update, then redirect
      setTimeout(() => {
        navigate('/app/dashboard', { replace: true });
      }, 1000);

      return { error: null };
    } catch (err) {
      return { error: { message: 'An unexpected error occurred during signup. Please try again.' } };
    }
  };

  return {
    invitation,
    loading,
    error,
    processInvitationSignup
  };
};
